import { useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, Box, Typography, useTheme
} from "@mui/material";

import { useTableUI } from "../contexts/TableUIContext";
import { Skeleton } from "@mui/material";
import DraggableRow from './TableDraggableRow';
import MemoizedRow from "./TableMemoizedRow"

const SelectablePaginatedTable = ({
  rows,
  columns,
  loading,
  collapsible = false,
  selactable = false,
  renderActions,
  renderInnerTable,
  innerTableKey,
  onSelectedIdsChange = () => { },
  rowSx = () => { },
  draggable = false,
}) => {

  const {
    selectedRows,
    setSelectedRows,
    collapsedRows,
    setCollapsedRows
  } = useTableUI();
  
  const theme = useTheme();


  const isSelected = (id) => selectedRows.includes(id);
  const isCollapsed = (id) => collapsedRows.includes(id);

  const handleSelectAll = (event) => {
    setSelectedRows(event.target.checked ? rows.map(r => r.id) : []);
  };

  const handleSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleCollapse = (id) => {
    setCollapsedRows(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const getColumnsCount = () => {
    return columns.length + 1 + !!selactable + !!collapsible;
  }

  useEffect(() => {
    onSelectedIdsChange?.(selectedRows);
  }, [selectedRows]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box component={Paper} sx={{ background: "transparent" }}>
      <TableContainer sx={{ 
        borderRadius: 1, 
        border: `1px solid ${theme.palette.divider}`,
        ...theme.applyStyles('dark', {
          border: '1px solid #242a37',
        }),
      }}>
        <Table sx={{
          tableLayout: 'auto'
        }}>
          <TableHead>
            <TableRow sx={{ 
              order: 1, 
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "#f5f6fa" 
            }}>
              {!!collapsible &&
                <TableCell padding="checkbox"></TableCell>
              }

              {!!selactable && <TableCell padding="checkbox">
                <Checkbox
                  sx={{ mx: 1, my: 0 }}
                  indeterminate={
                    selectedRows.length > 0 && selectedRows.length < rows.length
                  }
                  checked={
                    rows.length > 0 &&
                    selectedRows.length === rows.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>}

              {columns.map((column) => (
                <TableCell key={column.id} sx={{ py: 1, px: 2, textAlign: "center" }}>
                  <Typography sx={{ 
                    color: theme.palette.text.secondary, 
                    fontWeight: 500 
                  }}>{column.label}</Typography>
                </TableCell>
              ))}
              <TableCell sx={{ py: 1, px: 2 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  {Array.from(new Array(columns.length + 1 + !!selactable + !!collapsible)).map((col, i) => (
                    <TableCell key={i}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              !!rows.length ?
                rows.map((row) => {
                  const children = row[innerTableKey];
                  const RowWrapper = draggable ? DraggableRow : TableRow;

                  return (
                    <MemoizedRow
                      key={row.id}
                      row={row}
                      isSelected={isSelected(row.id)}
                      isCollapsed={isCollapsed(row.id)}
                      handleSelect={handleSelect}
                      handleCollapse={handleCollapse}
                      columns={columns}
                      collapsible={collapsible}
                      selactable={selactable}
                      renderActions={renderActions}
                      children={renderInnerTable ? renderInnerTable(row, children) : null}
                      RowWrapper={RowWrapper}
                      rowSx={rowSx(row)}
                    />
                  )
                }) : (
                  <TableRow>
                    <TableCell
                      colSpan={getColumnsCount()}
                      sx={{
                        borderBottom: "0 !important",
                        background: theme.palette.mode === 'dark' ? theme.palette.background.paper : "#fdfdfd",
                        p: 1.5
                      }}
                    >
                      <Typography align="center">No Data Found</Typography>
                    </TableCell>
                  </TableRow>
                )
            )}


          </TableBody>
        </Table>
      </TableContainer>


    </Box>
  );
};

export default SelectablePaginatedTable;
